import React, { useEffect, useState } from 'react';
import { Wrench, X, Check, MinusCircle } from 'lucide-react';
import ModalPortal from '../../components/ModalPortal';
import { fmtEuro } from './reparationsUtils';

const RepairLaborSuggestionModal = ({
  open,
  suggestion,
  partLabel,
  onClose,
  onApply,
  applying,
}) => {
  const [remise, setRemise] = useState(0);
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!open || !suggestion) return;
    setRemise(Number(suggestion.current_remise || 0));
    setNote('');
  }, [open, suggestion]);

  if (!open || !suggestion?.show) return null;

  const isSingle = suggestion.mode === 'single_part';
  const addedMo = Number(suggestion.added_mo || 0);
  const suggestedRemise = Number(suggestion.suggested_remise || 0);
  const proposedWaive = Number(suggestion.proposed_remise || 0);

  const waiveAddedMo = () => {
    setRemise(proposedWaive);
    setNote(
      isSingle
        ? `MO retirée pièce ${suggestion.added_reference || partLabel}`
        : `MO unique — ${suggestion.line_count} pièces`,
    );
  };

  return (
    <ModalPortal isOpen={open}>
      <div
        className="bg-[#151820] border border-white/10 rounded-2xl w-full max-w-md p-5 shadow-xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="repair-mo-suggestion-modal"
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 text-amber-300">
            <Wrench className="w-5 h-5 shrink-0" />
            <h3 className="font-bold text-white text-sm">Main d&apos;œuvre &amp; tarif client</h3>
          </div>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {partLabel && (
          <p className="text-xs text-cyan-400/90 mb-2">Pièce ajoutée : {partLabel}</p>
        )}

        <p className="text-sm text-gray-300 leading-relaxed mb-3">{suggestion.message}</p>

        <div className="mb-4 p-3 rounded-lg bg-black/30 border border-white/10 text-xs space-y-1.5">
          <div className="flex justify-between text-gray-400">
            <span>Total réparation (catalogue)</span>
            <span className="text-white tabular-nums font-semibold">{fmtEuro(suggestion.price_repair)} €</span>
          </div>
          {suggestion.quoted_total != null && (
            <div className="flex justify-between text-gray-500">
              <span>Devis initial</span>
              <span className="tabular-nums">{fmtEuro(suggestion.quoted_total)} €</span>
            </div>
          )}
          {addedMo > 0 && (
            <div className="flex justify-between text-amber-300/90">
              <span>MO estimée (pièce ajoutée)</span>
              <span className="tabular-nums">~{fmtEuro(addedMo)} €</span>
            </div>
          )}
          {!isSingle && suggestion.total_mo_catalog > 0 && (
            <div className="flex justify-between text-amber-300/90">
              <span>MO cumulée ({suggestion.line_count} pièces)</span>
              <span className="tabular-nums">{fmtEuro(suggestion.total_mo_catalog)} €</span>
            </div>
          )}
        </div>

        <div className="space-y-2 mb-4">
          <button
            type="button"
            onClick={onClose}
            className="w-full min-h-[48px] px-3 rounded-lg bg-emerald-950/50 border border-emerald-500/35 text-emerald-200 text-sm font-semibold hover:bg-emerald-950/70 flex items-center justify-center gap-2"
            data-testid="repair-mo-accept-catalog-btn"
          >
            <Check className="w-4 h-4" />
            {isSingle && addedMo > 0
              ? `Oui — facturer la MO incluse (prix catalogue ${fmtEuro(suggestion.added_unit_price)} €)`
              : 'Oui — garder le prix catalogue actuel'}
          </button>

          {suggestedRemise > 0 && (
            <button
              type="button"
              onClick={waiveAddedMo}
              className="w-full min-h-[48px] px-3 rounded-lg bg-amber-950/40 border border-amber-500/30 text-amber-200 text-sm font-semibold hover:bg-amber-950/60 flex items-center justify-center gap-2"
              data-testid="repair-mo-waive-btn"
            >
              <MinusCircle className="w-4 h-4" />
              {isSingle
                ? `Non — retirer la MO (~${fmtEuro(addedMo)} € de remise)`
                : `Non — une seule MO (${fmtEuro(suggestedRemise)} € de remise)`}
            </button>
          )}
        </div>

        <div className="border-t border-white/10 pt-3 space-y-3">
          <p className="text-[10px] font-bold text-gray-500 uppercase">Ou remise personnalisée</p>
          <div>
            <label className="block text-[10px] text-gray-500 mb-1">Remise totale sur la fiche (€)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={remise}
              onChange={(e) => setRemise(parseFloat(e.target.value) || 0)}
              className="w-full rounded-lg bg-black/30 border border-white/10 text-white px-3 py-2.5 text-sm"
            />
          </div>
          <div>
            <label className="block text-[10px] text-gray-500 mb-1">Note interne (optionnel)</label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Ex. accord client sans MO"
              className="w-full rounded-lg bg-black/30 border border-white/10 text-white px-3 py-2.5 text-sm"
            />
          </div>
          <button
            type="button"
            disabled={applying}
            onClick={() => onApply?.({ remise, note: note.trim() })}
            className="w-full min-h-[44px] rounded-lg bg-red-600 hover:bg-red-500 text-white text-sm font-semibold disabled:opacity-50"
            data-testid="repair-mo-apply-btn"
          >
            {applying ? 'Application…' : 'Appliquer cette remise'}
          </button>
        </div>

        <p className="text-[10px] text-gray-600 mt-3 text-center">
          Proposition staff — rien n&apos;est modifié sans votre validation.
        </p>
      </div>
    </ModalPortal>
  );
};

export default RepairLaborSuggestionModal;
